const heroTagName = 'jesoli-video-hero';
const heroSelector = `${heroTagName}[data-section-id]`;

class JesoliVideoHero extends HTMLElement {
  #abortController;
  #autoPlayTimer;
  #campaign;
  #campaignLink;
  #endPosterPreload;
  #fadeFrame;
  #hasAutoPlayed = false;
  #hasCampaign = false;
  #initialized = false;
  #media;
  #motionQuery;
  #onMotionChange;
  #replay;
  #resetTimer;
  #video;

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.destroy();
  }

  initialize() {
    if (this.#initialized) return;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const escapedSectionId = CSS.escape(sectionId);
    this.#media = this.querySelector(`[data-jesoli-hero-media="${escapedSectionId}"]`);
    this.#video = this.querySelector(`[data-jesoli-hero-video="${escapedSectionId}"]`);
    this.#replay = this.querySelector(`[data-jesoli-hero-replay="${escapedSectionId}"]`);
    this.#campaign = this.querySelector(`[data-jesoli-hero-campaign="${escapedSectionId}"]`);
    this.#campaignLink = this.querySelector(`[data-jesoli-hero-campaign-link="${escapedSectionId}"]`);

    if (!(this.#media instanceof HTMLElement) || !(this.#video instanceof HTMLVideoElement)) return;

    this.#hasCampaign = this.dataset.hasCampaign === 'true' && this.#campaign instanceof HTMLElement;

    this.#initialized = true;
    this.#abortController = new AbortController();
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#onMotionChange = (event) => this.#applyMotionPreference(event.matches);

    this.#video.addEventListener('ended', this.#resetToEndPoster, { signal: this.#abortController.signal });
    this.#replay?.addEventListener('click', this.#handleReplay, { signal: this.#abortController.signal });
    this.#motionQuery.addEventListener('change', this.#onMotionChange, { signal: this.#abortController.signal });
    for (const cta of this.querySelectorAll('[data-jesoli-hero-catalogue-target]')) {
      cta.addEventListener('click', this.#handleCatalogueCta, { signal: this.#abortController.signal });
    }

    this.#setReplayState(false);
    this.#applyMotionPreference(this.#motionQuery.matches);
  }

  destroy() {
    if (!this.#initialized) return;

    this.#initialized = false;
    this.#clearAsyncWork();
    this.#abortController?.abort();

    this.#video?.pause();

    this.#abortController = undefined;
    this.#campaign = undefined;
    this.#campaignLink = undefined;
    this.#endPosterPreload = undefined;
    this.#hasCampaign = false;
    this.#media = undefined;
    this.#motionQuery = undefined;
    this.#onMotionChange = undefined;
    this.#replay = undefined;
    this.#video = undefined;
  }

  #applyMotionPreference(isReduced) {
    if (!this.#media || !this.#video) return;

    if (isReduced) {
      this.#clearAsyncWork();
      this.#media.classList.add('has-ended');
      this.#media.classList.remove('is-start', 'is-playing', 'is-fading-out');
      this.#video.pause();
      if (this.#hasCampaign) this.#showCampaign();
      else this.#hideCampaign();
      this.#replay?.setAttribute('hidden', '');
      this.#setReplayState(false);
      return;
    }

    this.#replay?.removeAttribute('hidden');
    this.#setReplayState(false);

    if (this.#hasAutoPlayed && this.#hasCampaign) this.#showCampaign();
    else this.#hideCampaign();

    if (!this.#hasAutoPlayed) {
      this.#autoPlayTimer = window.setTimeout(() => this.#playOnce(), 1500);
    }
  }

  #clearAsyncWork() {
    window.clearTimeout(this.#autoPlayTimer);
    window.clearTimeout(this.#resetTimer);

    if (this.#fadeFrame !== undefined) {
      window.cancelAnimationFrame(this.#fadeFrame);
    }

    this.#autoPlayTimer = undefined;
    this.#fadeFrame = undefined;
    this.#resetTimer = undefined;
  }

  #handleReplay = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#playOnce({ manual: true });
  };

  #handleCatalogueCta = (event) => {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    const key = link.dataset.jesoliHeroCatalogueTarget || '';
    const catalogue = document.querySelector('jesoli-featured-catalogue[data-section-id]');
    if (!key || !catalogue || typeof catalogue.selectCategory !== 'function') return;
    if (typeof catalogue.hasCategory === 'function' && !catalogue.hasCategory(key)) return;

    event.preventDefault();
    catalogue.selectCategory(key, { focus: true, scroll: true });
  };

  #playOnce = async ({ manual = false } = {}) => {
    if (!this.#initialized || !this.#media || !this.#video || this.#motionQuery?.matches) return;
    if (!manual && this.#hasAutoPlayed) return;

    if (!manual) this.#hasAutoPlayed = true;

    this.#clearAsyncWork();
    this.#hideCampaign();
    this.#media.classList.remove('is-start', 'is-fading-out');

    try {
      this.#video.currentTime = 0;
    } catch {}

    this.#media.classList.add('is-playing');
    this.#setReplayState(true);

    try {
      await this.#video.play();
      this.#preloadEndPoster();
      if (!this.#initialized) this.#video.pause();
    } catch {
      this.#media.classList.remove('is-playing');
      this.#setReplayState(false);
    }
  };

  #preloadEndPoster() {
    if (this.#endPosterPreload) return;

    const endPosterUrl = this.dataset.endPosterUrl;
    if (!endPosterUrl) return;

    this.#endPosterPreload = new Image();
    this.#endPosterPreload.src = endPosterUrl;
  }

  #resetToEndPoster = () => {
    if (!this.#initialized || !this.#media || !this.#video) return;

    window.clearTimeout(this.#resetTimer);
    this.#media.classList.add('has-ended');
    this.#media.classList.remove('is-start');
    this.#showCampaign();
    this.#fadeFrame = window.requestAnimationFrame(() => {
      this.#media?.classList.add('is-fading-out');
      this.#media?.classList.remove('is-playing');
      this.#fadeFrame = undefined;
    });

    this.#resetTimer = window.setTimeout(() => {
      this.#video?.pause();
      this.#media?.classList.remove('is-fading-out');

      try {
        if (this.#video) this.#video.currentTime = 0;
      } catch {}

      this.#resetTimer = undefined;
    }, 1050);

    this.#setReplayState(false);
  };

  #showCampaign() {
    if (!this.#hasCampaign || !this.#media || !(this.#campaign instanceof HTMLElement)) return;

    this.#media.classList.add('is-campaign');
    this.classList.add('is-showing-campaign');
    this.#campaign.setAttribute('aria-hidden', 'false');

    if (this.#campaignLink instanceof HTMLAnchorElement) {
      this.#campaignLink.setAttribute('aria-hidden', 'false');
      this.#campaignLink.removeAttribute('tabindex');
    }
  }

  #hideCampaign() {
    this.#media?.classList.remove('is-campaign');
    this.classList.remove('is-showing-campaign');
    this.#campaign?.setAttribute('aria-hidden', 'true');

    if (this.#campaignLink instanceof HTMLAnchorElement) {
      this.#campaignLink.setAttribute('aria-hidden', 'true');
      this.#campaignLink.setAttribute('tabindex', '-1');
    }
  }

  #setReplayState(isPlaying) {
    if (!(this.#replay instanceof HTMLButtonElement)) return;

    const idleLabel = this.dataset.replayLabel || 'Watch the morning light';
    const playingLabel = this.dataset.playingLabel || 'Morning light playing';
    this.#replay.disabled = isPlaying;
    this.#replay.classList.toggle('is-playing', isPlaying);
    this.#replay.setAttribute('aria-label', isPlaying ? playingLabel : idleLabel);
  }
}

if (!customElements.get(heroTagName)) {
  customElements.define(heroTagName, JesoliVideoHero);
}

const getHeroes = (target) => {
  if (!(target instanceof Element)) return [];

  const heroes = [...target.querySelectorAll(heroSelector)];
  if (target.matches(heroSelector)) heroes.unshift(target);
  return heroes;
};

document.addEventListener('shopify:section:load', (event) => {
  for (const hero of getHeroes(event.target)) hero.initialize?.();
});

document.addEventListener('shopify:section:unload', (event) => {
  for (const hero of getHeroes(event.target)) hero.destroy?.();
});
